import {
  addDays,
  addMonths,
  differenceInDays,
  format,
  isBefore,
  isAfter,
  startOfDay,
} from 'date-fns/esm';
import template from './form.html';
import { pick } from 'lodash-es';
import {
  MOVE_DATE_MIN_ISO_8601,
  MOVE_DATE_MAX_ISO_8601,
  DATE_FORMATS,
  ITEM_DEFINITION_CODES,
  convertToISO8601,
} from '../../../../../constants';
import {
  fymDisclaimerRefresh,
  fymIndividualRefresh,
  fymFamilyRefresh,
  fymBusinessDisclaimer,
} from '../../../../../state/contentful/entries.json';
import { selectors, actions } from '../../../../../state';
import './form.global.css';
import '@updater-legacy/components/datepicker';
import '@updater-legacy/components/datepicker/popup';
import '@updater-legacy/components/datepicker/input';
import '@updater-components/radio-group';
import { initializePromotionsComponent } from '../promotions/initialize';

const START_DATE_DESCRIPTIONS = {
  PAST: `
    Your requested start date is in the past. The USPS should begin forwarding your mail within
    10-12 business days. Please note that the USPS does not retroactively forward mail.
  `,
  TODAY_AND_FUTURE: `
    Your start date should be the date you would like your mail forwarding to begin on.
    Depending on the location of your move, it can take up to 12 business days from
    your start date to begin receiving your forwarded mail at your new home.
  `,
};

function mapStateToThis(state) {
  const disclaimerEntry = selectors.contentful.getEntry(
    state,
    fymDisclaimerRefresh
  );
  const individualEntry = selectors.contentful.getEntry(
    state,
    fymIndividualRefresh
  );
  const familyEntry = selectors.contentful.getEntry(state, fymFamilyRefresh);
  const businessEntry = selectors.contentful.getEntry(
    state,
    fymBusinessDisclaimer
  );

  const itemDefinition = selectors.itemDefinition.byCode(
    ITEM_DEFINITION_CODES['FORWARD_MAIL']
  );

  const bannerHtml = itemDefinition.bannerHtml(state);

  return {
    fymDisclaimer: disclaimerEntry?.fields?.value,
    fymIndividual: individualEntry?.fields?.value,
    fymFamily: familyEntry?.fields?.value,
    fymBusiness: businessEntry?.fields?.value,
    bannerHtml,
  };
}

class ForwardMailController {
  requiredFields = ['base_street', 'city', 'state', 'zip'];
  dateRanges = {
    start: {
      min: MOVE_DATE_MIN_ISO_8601,
      max: MOVE_DATE_MAX_ISO_8601,
    },
    end: {
      min: undefined,
      max: undefined,
    },
  };

  constructor($element, $scope, updStore, $sce) {
    'ngInject';

    this.el = $element;
    this.scope = $scope;
    this.updStore = updStore;
    this.sce = $sce;
  }

  $onInit() {
    this.disconnect = this.updStore.connect(mapStateToThis)(this);
    if (!this.edit) {
      this.promotionsInteractor = initializePromotionsComponent(
        this.updStore,
        'fym_details',
        this.scope,
        'eligible'
      );
    }
    this.updStore.dispatch(
      actions.contentful.contentfulGet(fymDisclaimerRefresh)
    );
    this.updStore.dispatch(
      actions.contentful.contentfulGet(fymIndividualRefresh)
    );
    this.updStore.dispatch(actions.contentful.contentfulGet(fymFamilyRefresh));
    this.updStore.dispatch(
      actions.contentful.contentfulGet(fymBusinessDisclaimer)
    );
  }

  handleSubmit() {
    this.submit({ $value: this.fym });
    if (this.promotionsInteractor) {
      this.promotionsInteractor.claimPromotions();
    }
  }

  $onDestroy() {
    this.disconnect();
  }

  $postLink() {
    this.startDatepicker = this.el[0].querySelector(
      'upd-datepicker-input[name="startDate"]'
    );
    this.startDatepicker.addEventListener('input', (e) =>
      this.scope.$apply(() => this.onStartDateChange(e.target.value))
    );

    this.endDatepicker = this.el[0].querySelector(
      'upd-datepicker-input[name="endDate"]'
    );
    this.endDatepicker.addEventListener('input', (e) =>
      this.scope.$apply(this.onEndDateChange(e.target.value))
    );
    angular
      .element(this.endDatepicker)
      .controller('ngModel').$validators.withinRange = (
      modelValue,
      viewValue
    ) =>
      this.permanentMove ||
      !(
        isBefore(viewValue, this.dateRanges.end.min) ||
        isAfter(viewValue, this.dateRanges.end.max)
      );
  }

  $onChanges(changes) {
    const { item } = changes;

    if (item && item.currentValue) {
      const { currentValue } = item;

      this.fym = {
        from_address: {
          ...currentValue.from_address,
        },
        to_address: {
          ...currentValue.to_address,
        },
        ...pick(currentValue, [
          'form_type',
          'name_id',
          'start_date',
          'end_date',
          'business_name',
        ]),
      };

      this.setEndDateRanges(this.fym.start_date);

      if (item.isFirstChange()) {
        this.permanentMove = !this.fym.end_date;
      }
    }
  }

  onMoveDateChange(e) {
    this.permanentMove = e.target.value === 'true';
  }

  onEndDateChange(date) {
    this.fym.end_date = date;
  }

  isoToUSDate(isoDate) {
    return format(isoDate, 'MM/DD/YYYY');
  }

  onAddressChange(property, address) {
    this.fym = {
      ...this.fym,
      [property]: address,
    };
  }

  onNameChange(nameId) {
    this.fym.name_id = nameId;
  }

  onStartDateChange(date) {
    this.fym.start_date = date;
    this.setEndDateRanges(date);
  }

  setEndDateRanges(startDate) {
    // 15 Days after today
    const todayMin = addDays(new Date(), 15);

    // 15 Days after the start date
    const startMin = addDays(new Date(startDate), 15);

    // 15 days after today or 15 days after the start_date, whichever is later
    this.dateRanges.end.min = convertToISO8601(
      todayMin > startMin ? todayMin : startMin
    );

    // 6 months after the end date min
    this.dateRanges.end.max = convertToISO8601(
      addMonths(new Date(this.dateRanges.end.min), 6)
    );

    this.endDatepicker &&
      angular.element(this.endDatepicker).controller('ngModel').$validate();
  }

  onFormTypeChange(e) {
    const type = e.target.value;
    this.fym.form_type = type;
    if (type !== 'business') {
      this.fym.business_name = null;
    }
  }

  isFormType(type) {
    return this.fym.form_type === type;
  }

  get startDateDescription() {
    return dateIsInThePast(this.fym.start_date)
      ? START_DATE_DESCRIPTIONS.PAST
      : START_DATE_DESCRIPTIONS.TODAY_AND_FUTURE;
  }
}

export const ForwardMailFormComponent = {
  template,
  controller: ForwardMailController,
  bindings: {
    item: '<',
    submitting: '<',
    deleting: '<',
    edit: '<',
    addressesEditable: '<',
    submit: '&',
    delete: '&',
    cancelSref: '@',
  },
};

function dateIsInThePast(dateString) {
  return (
    differenceInDays(
      dateString,
      format(startOfDay(new Date()), DATE_FORMATS.ISO_8601)
    ) < 0
  );
}
